import { type ISODateType } from '@principle-theorem/shared';

export const EXACT_DEFAULT_PLAN_KEY = 'default';

/**
 * Maps an appointment to its treatment step, the opposite direction of
 * `patientTreatmentStepAppointment`.
 */
export const EXACT_APPOINTMENT_STEP_LINK_RESOURCE_TYPE =
  'patientAppointmentTreatmentStep';

/**
 * Source identifiers for Exact treatment steps. Every step id carries its plan
 * key, so steps from two plans on the same day can never share an id.
 */
export class ExactStepIdentity {
  static planKey(treatmentPlanId?: string | null): string {
    return treatmentPlanId ? treatmentPlanId : EXACT_DEFAULT_PLAN_KEY;
  }

  static completedStepUid(
    sourcePatientId: string,
    planKey: string,
    completedDate: ISODateType,
    appointmentUid?: string
  ): string {
    const stepUid = `${sourcePatientId}-plan-${planKey}-${completedDate}`;
    return appointmentUid ? `${stepUid}-apt-${appointmentUid}` : stepUid;
  }

  static bookedStepUid(
    sourcePatientId: string,
    planKey: string,
    appointmentUid: string
  ): string {
    return `${sourcePatientId}-plan-${planKey}-apt-${appointmentUid}`;
  }

  static plannedStepUid(
    sourcePatientId: string,
    planKey: string,
    plannedDate: ISODateType,
    visitSequence: string
  ): string {
    return `${sourcePatientId}-plan-${planKey}-planned-${plannedDate}-${visitSequence}`;
  }

  static undatedStepUid(sourcePatientId: string, planKey: string): string {
    return `${sourcePatientId}-plan-${planKey}-incomplete`;
  }

  /**
   * Placeholder steps live in the default plan and are named after their
   * appointment, but are deliberately not anchored to it (see
   * `isAnchoredToAppointment`).
   */
  static placeholderStepUid(
    sourcePatientId: string,
    appointmentUid: string
  ): string {
    return `${sourcePatientId}-plan-${EXACT_DEFAULT_PLAN_KEY}-appointment-${appointmentUid}`;
  }

  static appointmentLinkKey(
    sourcePatientId: string,
    appointmentUid: string
  ): string {
    return `${sourcePatientId}-appointment-${appointmentUid}`;
  }

  static isAnchoredToAppointment(
    stepUid: string,
    appointmentUid: string
  ): boolean {
    return stepUid.endsWith(`-apt-${appointmentUid}`);
  }
}
